import { useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Search, SearchX } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { adminSearch } from "@/api/admin";
import type { AdminSearchItem } from "@/types/admin";
import { useAdminBasePath } from "@/contexts/AdminBasePathContext";
import { rewriteAlertLink } from "@/features/admin/components/alertLink";
import {
  buildCommandGroups,
  COMMAND_TYPE_META,
  flattenCommandResults,
} from "@/features/admin/command/commandPaletteResults";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";

/** Minimum query length before the search endpoint is hit. */
const MIN_QUERY_LENGTH = 2;
/** Debounce applied to the raw input before it becomes the query key. */
const DEBOUNCE_MS = 250;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * CommandPalette — the Cmd/Ctrl+K global admin search modal (Sprint 142,
 * TASK-4221). Built in-house on `@radix-ui/react-dialog` (focus trap, focus
 * return, Esc-to-close) plus a plain keyboard-driven `role="listbox"` — no
 * `cmdk` / new dependency.
 *
 * Query is debounced (~250ms) and only fetched at `q.length >= 2`; results are
 * grouped (tenants / users / dealers / invoices) with muted headers + per-type
 * icons. ArrowUp/Down move `activeIndex` across the FLATTENED item list
 * (wrapping, group headers skipped); Enter navigates to the active item's URL
 * (rewritten to the active shell base path); hover/click select. Empty query
 * shows a hint (no fetch); loading shows skeleton rows; no results shows an
 * EmptyState. An `aria-live` region announces the result count.
 */
export function CommandPalette({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const basePath = useAdminBasePath();

  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset transient state each time the palette opens so it never re-opens with
  // a stale query or highlight.
  useEffect(() => {
    if (open) {
      setRawQuery("");
      setDebouncedQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Debounce the raw input into the query key.
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedQuery(rawQuery.trim()),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [rawQuery]);

  const enabled = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, isError } = useQuery({
    queryKey: ["admin", "search", debouncedQuery],
    queryFn: () => adminSearch(debouncedQuery),
    enabled,
    staleTime: 30_000,
  });

  const groups = useMemo(() => buildCommandGroups(data), [data]);
  const flatResults = useMemo(() => flattenCommandResults(data), [data]);

  // Keep the active index within bounds as results change.
  useEffect(() => {
    setActiveIndex((prev) =>
      flatResults.length === 0 ? 0 : Math.min(prev, flatResults.length - 1),
    );
  }, [flatResults.length]);

  function selectItem(item: AdminSearchItem) {
    const target = rewriteAlertLink(item.url, basePath);
    onOpenChange(false);
    if (target) {
      void navigate(target);
    }
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (flatResults.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % flatResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + flatResults.length) % flatResults.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = flatResults[activeIndex];
      if (item) selectItem(item);
    }
  }

  const showHint = debouncedQuery.length < MIN_QUERY_LENGTH;
  const showLoading = enabled && isFetching && flatResults.length === 0;
  const showEmpty =
    enabled && !isFetching && (isError || flatResults.length === 0);

  // Running flat index so each rendered row can compare against `activeIndex`.
  let runningIndex = -1;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          aria-label={t("commandPalette.dialogLabel")}
          className="fixed left-1/2 top-24 z-50 w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl focus:outline-none"
        >
          <Dialog.Title className="sr-only">
            {t("commandPalette.dialogLabel")}
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            {t("commandPalette.dialogDescription")}
          </Dialog.Description>

          {/* Search input — owns the real focus; controls the listbox. */}
          <div className="flex items-center gap-2 border-b border-gray-100 px-4">
            <Search
              size={18}
              aria-hidden="true"
              className="shrink-0 text-gray-400"
            />
            <input
              // A command palette must focus its input on open; Radix traps
              // focus inside the dialog so this does not steal focus from the
              // wider page.
              autoFocus
              type="text"
              role="combobox"
              aria-expanded={flatResults.length > 0}
              aria-controls="command-palette-listbox"
              aria-label={t("commandPalette.inputLabel")}
              placeholder={t("commandPalette.inputPlaceholder")}
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              className="w-full bg-transparent py-3.5 text-sm text-gray-900 outline-none placeholder:text-gray-400"
            />
          </div>

          {/* Result count announcement for assistive tech. */}
          <div aria-live="polite" className="sr-only">
            {enabled && !isFetching
              ? t("commandPalette.resultCount", { count: flatResults.length })
              : ""}
          </div>

          <div className="max-h-80 overflow-y-auto p-2">
            {showHint ? (
              <p className="px-3 py-6 text-center text-sm text-gray-400">
                {t("commandPalette.hint")}
              </p>
            ) : showLoading ? (
              <ul className="space-y-1" aria-hidden="true">
                {Array.from({ length: 5 }, (_, i) => (
                  <li key={i} className="flex items-center gap-3 px-3 py-2">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-4 w-40" />
                  </li>
                ))}
              </ul>
            ) : showEmpty ? (
              <EmptyState
                icon={SearchX}
                title={t("commandPalette.noResults")}
                subtitle={t("commandPalette.noResultsSubtitle", {
                  query: debouncedQuery,
                })}
              />
            ) : (
              <ul
                ref={listRef}
                id="command-palette-listbox"
                role="listbox"
                aria-label={t("commandPalette.resultsLabel")}
              >
                {groups.map((group) => {
                  const { icon: Icon, labelKey } =
                    COMMAND_TYPE_META[group.type];
                  return (
                    <li key={group.type} role="presentation">
                      <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {t(labelKey)}
                      </p>
                      <ul role="presentation">
                        {group.items.map((item) => {
                          runningIndex += 1;
                          const index = runningIndex;
                          const isActive = index === activeIndex;
                          return (
                            <li
                              key={`${item.type}-${item.id}`}
                              role="option"
                              aria-selected={isActive}
                              onMouseEnter={() => setActiveIndex(index)}
                              onClick={() => selectItem(item)}
                              className={cn(
                                "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2",
                                isActive ? "bg-blue-50" : "hover:bg-gray-50",
                              )}
                            >
                              <Icon
                                size={16}
                                aria-hidden="true"
                                className={cn(
                                  "shrink-0",
                                  isActive
                                    ? "text-brand-primary"
                                    : "text-gray-400",
                                )}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-gray-900">
                                  {item.label}
                                </span>
                                {item.sublabel ? (
                                  <span className="block truncate text-xs text-gray-500">
                                    {item.sublabel}
                                  </span>
                                ) : null}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
